/**
 * 🌟 SOUL ASPIRATIONS QUIZ
 * 
 * "Qui veux-tu devenir ? Quels sont tes rêves ?"
 * 
 * Quiz en 3 étapes pour découvrir les aspirations profondes :
 * 1. Rêves de vie (3-5 choix)
 * 2. Valeurs fondamentales (3-5 choix)
 * 3. Domaines de croissance (2-3 choix)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../../ui/button';
import { ChevronRight, ChevronLeft, Sparkles, Check } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';

// ============================================
// DONNÉES DES OPTIONS
// ============================================

const LIFE_DREAMS = [
  { id: 'adventure', emoji: '🌍', label: 'Voyager et explorer le monde' },
  { id: 'family', emoji: '👨‍👩‍👧‍👦', label: 'Fonder une famille épanouie' },
  { id: 'career', emoji: '🚀', label: 'Réussir professionnellement' },
  { id: 'entrepreneur', emoji: '💡', label: 'Créer ma propre entreprise' },
  { id: 'art', emoji: '🎨', label: 'Vivre de ma passion créative' },
  { id: 'impact', emoji: '🌱', label: 'Changer le monde positivement' },
  { id: 'wisdom', emoji: '📚', label: 'Cultiver la sagesse' },
  { id: 'freedom', emoji: '🦋', label: 'Liberté totale' },
  { id: 'love', emoji: '💜', label: 'Trouver l\'amour de ma vie' },
  { id: 'health', emoji: '🧘', label: 'Santé et bien-être optimal' },
  { id: 'wealth', emoji: '💎', label: 'Sécurité financière' },
  { id: 'community', emoji: '🤝', label: 'Créer une communauté' },
];

const CORE_VALUES = [
  { id: 'authenticity', emoji: '🎭', label: 'Authenticité' },
  { id: 'loyalty', emoji: '🤝', label: 'Loyauté' },
  { id: 'freedom', emoji: '🦅', label: 'Liberté' },
  { id: 'respect', emoji: '🙏', label: 'Respect' },
  { id: 'humor', emoji: '😂', label: 'Humour' },
  { id: 'ambition', emoji: '🎯', label: 'Ambition' },
  { id: 'kindness', emoji: '💕', label: 'Bienveillance' },
  { id: 'courage', emoji: '🦁', label: 'Courage' },
  { id: 'creativity', emoji: '✨', label: 'Créativité' },
  { id: 'honesty', emoji: '💎', label: 'Honnêteté' },
  { id: 'patience', emoji: '🌳', label: 'Patience' },
  { id: 'passion', emoji: '🔥', label: 'Passion' },
];

const GROWTH_AREAS = [
  { id: 'confidence', emoji: '💪', label: 'Confiance en moi' },
  { id: 'communication', emoji: '💬', label: 'Communication' },
  { id: 'emotional', emoji: '💜', label: 'Intelligence émotionnelle' },
  { id: 'boundaries', emoji: '🛡️', label: 'Poser mes limites' },
  { id: 'vulnerability', emoji: '🌸', label: 'Accepter ma vulnérabilité' },
  { id: 'letting_go', emoji: '🍃', label: 'Lâcher prise' },
  { id: 'self_love', emoji: '🪞', label: 'M\'aimer moi-même' },
  { id: 'presence', emoji: '🧘', label: 'Vivre le moment présent' },
  { id: 'discipline', emoji: '⚡', label: 'Discipline personnelle' },
  { id: 'forgiveness', emoji: '🕊️', label: 'Pardonner' },
  { id: 'independence', emoji: '🚀', label: 'Indépendance' },
  { id: 'openness', emoji: '🌈', label: 'Ouverture d\'esprit' },
];

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export const SoulAspirationsQuiz = ({ 
  customerId, 
  onComplete, 
  onSkip,
  isDarkMode = false 
}) => {
  const [step, setStep] = useState(0); // 0: intro, 1: dreams, 2: values, 3: growth, 4: complete
  const [selectedDreams, setSelectedDreams] = useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [selectedGrowth, setSelectedGrowth] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const steps = [
    { id: 'intro', title: 'Bienvenue', icon: '✨' },
    { id: 'dreams', title: 'Tes rêves', icon: '🌟', data: LIFE_DREAMS, selected: selectedDreams, setSelected: setSelectedDreams, min: 2, max: 5 },
    { id: 'values', title: 'Tes valeurs', icon: '💎', data: CORE_VALUES, selected: selectedValues, setSelected: setSelectedValues, min: 2, max: 5 },
    { id: 'growth', title: 'Ta croissance', icon: '🌱', data: GROWTH_AREAS, selected: selectedGrowth, setSelected: setSelectedGrowth, min: 1, max: 3 },
    { id: 'complete', title: 'Terminé', icon: '🎉' },
  ];

  const currentStep = steps[step];
  const canProceed = step === 0 || step === 4 || 
    (currentStep.selected && currentStep.selected.length >= currentStep.min);

  const handleToggleItem = (itemId) => {
    const { selected, setSelected, max } = currentStep;
    if (selected.includes(itemId)) {
      setSelected(selected.filter(id => id !== itemId));
    } else if (selected.length < max) {
      setSelected([...selected, itemId]);
    }
  };

  const handleNext = async () => {
    if (step === 3) {
      // Soumettre les données
      setIsSubmitting(true);
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        await axios.post(`${API_URL}/api/soul/aspirations/${customerId}`, {
          dreams: selectedDreams,
          values: selectedValues,
          growthAreas: selectedGrowth
        });
        
        // Confetti !
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#EC4899', '#8B5CF6', '#3B82F6', '#10B981']
        });
        
        setStep(4);
      } catch (error) {
        console.error('Erreur soumission aspirations:', error);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  return (
    <div className={`min-h-[500px] flex flex-col ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
      {/* Progress bar */}
      <div className="flex gap-1 mb-6">
        {steps.slice(1, 4).map((s, i) => (
          <div 
            key={s.id}
            className={`h-1 flex-1 rounded-full transition-all ${
              i < step ? 'bg-pink-500' : i === step - 1 ? 'bg-pink-300' : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {/* Intro */}
        {step === 0 && (
          <motion.div
            key="intro"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="flex-1 flex flex-col items-center justify-center text-center px-4"
          >
            <span className="text-6xl mb-4">✨</span>
            <h2 className="text-2xl font-bold mb-2">Qui veux-tu devenir ?</h2>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Quelques questions pour découvrir tes aspirations profondes et te matcher avec des personnes qui partagent tes rêves...
            </p>
            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              ⏱️ ~2 minutes
            </p>
          </motion.div>
        )}

        {/* Selection steps (dreams, values, growth) */}
        {step >= 1 && step <= 3 && (
          <motion.div
            key={currentStep.id}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="flex-1 flex flex-col"
          >
            <div className="text-center mb-4">
              <span className="text-4xl mb-2 block">{currentStep.icon}</span>
              <h2 className="text-xl font-bold">
                {step === 1 && "Quels sont tes rêves de vie ?"}
                {step === 2 && "Quelles valeurs te définissent ?"}
                {step === 3 && "Où veux-tu grandir ?"}
              </h2>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Choisis {currentStep.min}-{currentStep.max} réponses
              </p>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="grid grid-cols-2 gap-2">
                {currentStep.data.map((item) => {
                  const isSelected = currentStep.selected.includes(item.id);
                  return (
                    <motion.button
                      key={item.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleToggleItem(item.id)}
                      className={`p-3 rounded-xl text-left transition-all flex items-center gap-2 ${
                        isSelected
                          ? 'bg-gradient-to-r from-pink-500 to-purple-500 text-white shadow-lg'
                          : isDarkMode 
                            ? 'bg-gray-800 hover:bg-gray-700' 
                            : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <span className="text-xl">{item.emoji}</span>
                      <span className="text-sm font-medium flex-1">{item.label}</span>
                      {isSelected && <Check className="w-4 h-4" />}
                    </motion.button>
                  );
                })}
              </div>
            </div>

            <div className={`mt-4 text-center text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {currentStep.selected.length} / {currentStep.max} sélectionnés
            </div>
          </motion.div>
        )}

        {/* Complete */}
        {step === 4 && (
          <motion.div
            key="complete"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex-1 flex flex-col items-center justify-center text-center px-4"
          >
            <span className="text-6xl mb-4">🎉</span>
            <h2 className="text-2xl font-bold mb-2">Parfait !</h2>
            <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Tes aspirations vont nous aider à te trouver des personnes qui partagent tes rêves !
            </p>
            <div className="flex flex-wrap gap-2 justify-center mb-6">
              {[...selectedDreams, ...selectedValues].slice(0, 6).map(id => {
                const item = [...LIFE_DREAMS, ...CORE_VALUES].find(i => i.id === id);
                return item ? (
                  <span key={id} className="px-3 py-1 rounded-full bg-pink-100 text-pink-700 text-sm">
                    {item.emoji} {item.label}
                  </span>
                ) : null;
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation buttons */}
      <div className="flex gap-3 mt-4">
        {step > 0 && step < 4 && (
          <Button
            variant="outline"
            onClick={handleBack}
            className={isDarkMode ? 'border-gray-600' : ''}
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Retour
          </Button>
        )}
        
        <Button
          onClick={step === 4 ? onComplete : handleNext}
          disabled={!canProceed || isSubmitting}
          className="flex-1 bg-gradient-to-r from-pink-500 to-purple-500 text-white"
        >
          {isSubmitting ? (
            <span className="animate-pulse">Enregistrement...</span>
          ) : step === 0 ? (
            <>C'est parti ! <Sparkles className="w-4 h-4 ml-1" /></>
          ) : step === 4 ? (
            <>Continuer <ChevronRight className="w-4 h-4 ml-1" /></>
          ) : step === 3 ? (
            <>Terminer <Check className="w-4 h-4 ml-1" /></>
          ) : (
            <>Suivant <ChevronRight className="w-4 h-4 ml-1" /></>
          )}
        </Button>
      </div>

      {/* Skip button */}
      {step < 4 && onSkip && (
        <button
          onClick={onSkip}
          className={`mt-3 text-sm ${isDarkMode ? 'text-gray-500 hover:text-gray-400' : 'text-gray-400 hover:text-gray-500'}`}
        >
          Passer pour l'instant
        </button>
      )}
    </div>
  );
};

export default SoulAspirationsQuiz;
